import ExcelJS from 'exceljs';

/**
 * Branded Pricy Excel export for a product.
 *
 * 4 sheets:
 *   1. Summary       — product metadata + RRPs + period stats
 *   2. Raw history   — one row per (date, reseller) scrape
 *   3. Pivot         — date × reseller matrix of discount %
 *   4. Averages      — avg / min / max discount per reseller
 *
 * All numeric cells are stored as real numbers and formatted (money / %).
 * Amber palette matches the Pricy brand.
 */

// ---- palette (Pricy amber) ----
const AMBER_500 = 'FFF59E0B';   // vibrant amber — main title band
const AMBER_100 = 'FFFEF3C7';   // pale amber — header row backgrounds
const AMBER_50  = 'FFFFFBEB';   // very pale — zebra rows
const AMBER_800 = 'FF78350F';   // deep amber — header text
const AMBER_900 = 'FF451A03';   // near-black amber — titles
const WHITE     = 'FFFFFFFF';
const GREY_200  = 'FFE5E7EB';   // borders
const GREY_500  = 'FF6B7280';   // subtitle text
const RED_700   = 'FFB91C1C';   // negative accent (violations)

const FONT_TITLE     = { name: 'Calibri', size: 22, bold: true, color: { argb: WHITE } };
const FONT_SUBTITLE  = { name: 'Calibri', size: 12, color: { argb: WHITE } };
const FONT_SECTION   = { name: 'Calibri', size: 11, bold: true, color: { argb: AMBER_900 } };
const FONT_HEADER    = { name: 'Calibri', size: 11, bold: true, color: { argb: AMBER_800 } };
const FONT_LABEL     = { name: 'Calibri', size: 10, color: { argb: GREY_500 } };
const FONT_VALUE     = { name: 'Calibri', size: 11, bold: true };
const FONT_BODY      = { name: 'Calibri', size: 10 };

const FILL_AMBER_500 = { type: 'pattern', pattern: 'solid', fgColor: { argb: AMBER_500 } };
const FILL_AMBER_100 = { type: 'pattern', pattern: 'solid', fgColor: { argb: AMBER_100 } };
const FILL_AMBER_50  = { type: 'pattern', pattern: 'solid', fgColor: { argb: AMBER_50 } };
const BORDER_LIGHT   = { style: 'thin', color: { argb: GREY_200 } };
const ALL_BORDERS    = { top: BORDER_LIGHT, left: BORDER_LIGHT, bottom: BORDER_LIGHT, right: BORDER_LIGHT };

// ---- number formats ----
const FMT_EUR      = '#,##0.00\\ [$€]';
const FMT_PCT_TXT  = '0.00\\ "%"';
const FMT_INT      = '#,##0';

// Excel forbids : \ / ? * [ ] in sheet names, max 31 chars.
function sheetName(name) {
  return (name || 'Sheet').replace(/[:\\/?*[\]]/g, '_').slice(0, 31);
}

function toNumberOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : Math.round(n * 100) / 100;
}

// Apply a compact title band across the sheet.
function addTitleBand(ws, columns, title, subtitle) {
  ws.mergeCells(1, 1, 1, columns);
  const t = ws.getCell(1, 1);
  t.value = title;
  t.font = FONT_TITLE;
  t.fill = FILL_AMBER_500;
  t.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  ws.getRow(1).height = 34;

  ws.mergeCells(2, 1, 2, columns);
  const s = ws.getCell(2, 1);
  s.value = subtitle;
  s.font = FONT_SUBTITLE;
  s.fill = FILL_AMBER_500;
  s.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  ws.getRow(2).height = 22;

  ws.getRow(3).height = 8; // spacer
}

// Style a table header row (returns nothing).
function styleHeaderRow(ws, rowIdx, columnCount) {
  const row = ws.getRow(rowIdx);
  row.height = 24;
  for (let c = 1; c <= columnCount; c++) {
    const cell = row.getCell(c);
    cell.font = FONT_HEADER;
    cell.fill = FILL_AMBER_100;
    cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    cell.border = ALL_BORDERS;
  }
}

// Apply zebra + border to every row in the range.
function styleBodyRows(ws, startRow, endRow, columnCount) {
  for (let r = startRow; r <= endRow; r++) {
    const row = ws.getRow(r);
    const zebra = (r - startRow) % 2 === 1;
    for (let c = 1; c <= columnCount; c++) {
      const cell = row.getCell(c);
      cell.font = cell.font || FONT_BODY;
      cell.border = ALL_BORDERS;
      if (zebra) cell.fill = FILL_AMBER_50;
      cell.alignment = cell.alignment || { vertical: 'middle', indent: 1 };
    }
  }
}


// ============================================================
//  Build the workbook
// ============================================================
export async function downloadProductWorkbook({ product, filteredHistory, timeRange, t }) {
  const range = timeRange || '90d';
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Pricy by LampTwist';
  wb.created = new Date();

  const rows = Array.isArray(filteredHistory) ? filteredHistory : [];
  const dateExport = new Date().toISOString().slice(0, 10);
  const productLabel = product?.p_title || product?.v_sku || 'Product';

  const uniqueResellers = Array.from(new Set(rows.map(r => r.hostname).filter(Boolean))).sort();
  const uniqueDates = Array.from(new Set(rows.map(r => r.date).filter(Boolean))).sort();
  const allDiscounts = rows.map(r => parseFloat(r.discount)).filter(n => !Number.isNaN(n));

  buildSummarySheet({ wb, product, productLabel, dateExport, range, uniqueResellers, uniqueDates, rows, allDiscounts, t });
  buildRawSheet({ wb, rows, productLabel, range, t });
  buildPivotSheet({ wb, rows, uniqueResellers, uniqueDates, productLabel, range, t });
  buildAveragesSheet({ wb, rows, uniqueResellers, productLabel, range, t });

  const buffer = await wb.xlsx.writeBuffer();
  const filename = buildFilename({ product, range, dateExport, t });
  triggerDownload(buffer, filename);
}

/**
 * Build a human-readable filename in the user's language:
 *   "Pricy - {product name} - {localized period} - {YYYY-MM-DD}.xlsx"
 *
 * Falls back to SKU / handle / shopify_id when the product has no title,
 * and sanitizes characters forbidden on Windows (\ / : * ? " < > |).
 */
function buildFilename({ product, range, dateExport, t }) {
  const rawName =
    product?.p_title ||
    product?.v_sku ||
    product?.p_handle ||
    (product?.shopify_id ? String(product.shopify_id) : 'Product');

  const periodLabel = t(`timeRange.${range}`) || range;

  return `${sanitizeFilenamePart('Pricy')} - ${sanitizeFilenamePart(rawName, 60)} - ${sanitizeFilenamePart(periodLabel)} - ${dateExport}.xlsx`;
}

function sanitizeFilenamePart(input, maxLen = 80) {
  if (input === null || input === undefined) return '';
  return String(input)
    // Strip Windows-forbidden filename chars (and control chars)
    .replace(/[\\/:*?"<>|\x00-\x1F]+/g, '-')
    // Collapse consecutive whitespace
    .replace(/\s+/g, ' ')
    // Collapse runs of dashes and trim edges
    .replace(/-+/g, '-')
    .trim()
    .slice(0, maxLen)
    .trim()
    // Remove leading/trailing dots or dashes so the file opens cleanly on all OSes
    .replace(/^[\s.-]+|[\s.-]+$/g, '');
}

function triggerDownload(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}


// ---------- Sheet 1 : Summary ----------
function buildSummarySheet({ wb, product, productLabel, dateExport, range, uniqueResellers, uniqueDates, rows, allDiscounts, t }) {
  const ws = wb.addWorksheet(sheetName(t('exportSheet.summary')), {
    views: [{ showGridLines: false }],
  });

  const COLS = 2;
  ws.columns = [{ width: 36 }, { width: 42 }];

  addTitleBand(ws, COLS, `Pricy — ${productLabel}`, `${t('exportSummary.period')}: ${t(`timeRange.${range}`) || range} · ${t('exportSummary.exportDate')}: ${dateExport}`);

  let r = 4;
  const section = (label) => {
    ws.mergeCells(r, 1, r, COLS);
    const c = ws.getCell(r, 1);
    c.value = label;
    c.font = FONT_SECTION;
    c.fill = FILL_AMBER_100;
    c.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    c.border = ALL_BORDERS;
    ws.getRow(r).height = 22;
    r++;
  };

  const kv = (label, value, opts = {}) => {
    const rowRef = ws.getRow(r);
    const lblCell = rowRef.getCell(1);
    const valCell = rowRef.getCell(2);
    lblCell.value = label;
    lblCell.font = FONT_LABEL;
    lblCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    lblCell.border = ALL_BORDERS;
    valCell.value = value ?? '';
    valCell.font = FONT_VALUE;
    valCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    valCell.border = ALL_BORDERS;
    if (opts.numFmt) valCell.numFmt = opts.numFmt;
    if (opts.color) valCell.font = { ...FONT_VALUE, color: { argb: opts.color } };
    r++;
  };

  section(t('exportSummary.title'));
  kv(t('exportSummary.title'), product?.p_title || '');
  kv(t('exportSummary.sku'), product?.v_sku || '');
  kv(t('exportSummary.ean'), product?.v_ean || '');
  kv(t('exportSummary.vendorCode'), product?.v_vendor_code || '');
  kv(t('exportSummary.vendor'), product?.p_vendor || '');
  r++;

  section('RRP');
  kv(t('exportSummary.rrpBE'), toNumberOrNull(product?.compareAt_BE), { numFmt: FMT_EUR });
  kv(t('exportSummary.rrpNL'), toNumberOrNull(product?.compareAt_NL), { numFmt: FMT_EUR });
  kv(t('exportSummary.rrpFR'), toNumberOrNull(product?.compareAt_FR), { numFmt: FMT_EUR });
  kv(t('exportSummary.rrpDE'), toNumberOrNull(product?.compareAt_DE), { numFmt: FMT_EUR });
  kv(t('exportSummary.rrpUK'), toNumberOrNull(product?.compareAt_UK), { numFmt: '#,##0.00\\ [$£]' });
  kv(t('exportSummary.rrpDefault'), toNumberOrNull(product?.v_compareAtPrice), { numFmt: FMT_EUR });
  r++;

  section(t('exportSummary.period'));
  kv(t('exportSummary.period'), t(`timeRange.${range}`) || range);
  kv(t('exportSummary.exportDate'), dateExport);
  kv(t('exportSummary.resellersTracked'), uniqueResellers.length, { numFmt: FMT_INT });
  kv(t('exportSummary.daysObserved'), uniqueDates.length, { numFmt: FMT_INT });
  kv(t('exportSummary.totalRows'), rows.length, { numFmt: FMT_INT });

  if (allDiscounts.length) {
    r++;
    section(t('exportSummary.avgDiscount'));
    const avg = allDiscounts.reduce((s, n) => s + n, 0) / allDiscounts.length;
    const min = Math.min(...allDiscounts);
    const max = Math.max(...allDiscounts);
    kv(t('exportSummary.avgDiscount'), Math.round(avg * 100) / 100, { numFmt: FMT_PCT_TXT, color: RED_700 });
    kv(t('exportSummary.minDiscount'), Math.round(min * 100) / 100, { numFmt: FMT_PCT_TXT });
    kv(t('exportSummary.maxDiscount'), Math.round(max * 100) / 100, { numFmt: FMT_PCT_TXT, color: RED_700 });
  }

  ws.views = [{ state: 'frozen', ySplit: 3, showGridLines: false }];
}


// ---------- Sheet 2 : Raw history ----------
function buildRawSheet({ wb, rows, productLabel, range, t }) {
  const ws = wb.addWorksheet(sheetName(t('exportSheet.raw')), {
    views: [{ showGridLines: false }],
  });

  const headers = [
    t('exportRaw.date'),
    t('exportRaw.reseller'),
    t('exportRaw.country'),
    t('exportRaw.priceReseller'),
    t('exportRaw.rrp'),
    t('exportRaw.discount'),
  ];

  ws.columns = [
    { width: 14 }, { width: 26 }, { width: 12 }, { width: 20 }, { width: 20 }, { width: 18 },
  ];

  addTitleBand(ws, headers.length, `${t('exportSheet.raw')} — ${productLabel}`, `${t('exportSummary.period')}: ${t(`timeRange.${range}`) || range}`);

  const headerRowIdx = 4;
  const headerRow = ws.getRow(headerRowIdx);
  headers.forEach((h, i) => (headerRow.getCell(i + 1).value = h));
  styleHeaderRow(ws, headerRowIdx, headers.length);

  const sorted = [...rows].sort((a, b) => {
    if (a.date === b.date) return (a.hostname || '').localeCompare(b.hostname || '');
    return (b.date || '').localeCompare(a.date || '');
  });

  const startRow = headerRowIdx + 1;
  sorted.forEach((r, idx) => {
    const rowRef = ws.getRow(startRow + idx);
    rowRef.getCell(1).value = r.date || '';
    rowRef.getCell(2).value = r.hostname || '';
    rowRef.getCell(3).value = (r.country || '').toUpperCase();
    rowRef.getCell(4).value = toNumberOrNull(r.price);
    rowRef.getCell(4).numFmt = FMT_EUR;
    rowRef.getCell(5).value = toNumberOrNull(r.compare_at_price);
    rowRef.getCell(5).numFmt = FMT_EUR;
    rowRef.getCell(6).value = toNumberOrNull(r.discount);
    rowRef.getCell(6).numFmt = FMT_PCT_TXT;
    // Highlight discount in red when positive (= reseller below RRP)
    if (typeof rowRef.getCell(6).value === 'number' && rowRef.getCell(6).value > 0) {
      rowRef.getCell(6).font = { ...FONT_BODY, bold: true, color: { argb: RED_700 } };
    }
  });

  if (sorted.length) {
    styleBodyRows(ws, startRow, startRow + sorted.length - 1, headers.length);
  }

  ws.autoFilter = {
    from: { row: headerRowIdx, column: 1 },
    to: { row: headerRowIdx + Math.max(sorted.length, 1), column: headers.length },
  };

  ws.views = [{ state: 'frozen', xSplit: 0, ySplit: headerRowIdx, showGridLines: false }];
}


// ---------- Sheet 3 : Pivot ----------
function buildPivotSheet({ wb, rows, uniqueResellers, uniqueDates, productLabel, range, t }) {
  const ws = wb.addWorksheet(sheetName(t('exportSheet.pivot')), {
    views: [{ showGridLines: false }],
  });

  const headers = [t('exportRaw.date'), ...uniqueResellers];
  ws.columns = [{ width: 14 }, ...uniqueResellers.map(() => ({ width: 16 }))];

  addTitleBand(ws, headers.length, `${t('exportSheet.pivot')} — ${productLabel}`, `${t('exportSummary.period')}: ${t(`timeRange.${range}`) || range}`);

  const headerRowIdx = 4;
  const headerRow = ws.getRow(headerRowIdx);
  headers.forEach((h, i) => (headerRow.getCell(i + 1).value = h));
  styleHeaderRow(ws, headerRowIdx, headers.length);

  const pivot = new Map();
  for (const r of rows) {
    if (!r.date || !r.hostname) continue;
    if (!pivot.has(r.date)) pivot.set(r.date, new Map());
    pivot.get(r.date).set(r.hostname, toNumberOrNull(r.discount));
  }

  const startRow = headerRowIdx + 1;
  uniqueDates.forEach((date, idx) => {
    const rowRef = ws.getRow(startRow + idx);
    rowRef.getCell(1).value = date;
    const perReseller = pivot.get(date) || new Map();
    uniqueResellers.forEach((rs, cIdx) => {
      const val = perReseller.get(rs);
      const cell = rowRef.getCell(cIdx + 2);
      if (val !== null && val !== undefined) {
        cell.value = val;
        cell.numFmt = FMT_PCT_TXT;
        if (typeof val === 'number' && val > 0) {
          cell.font = { ...FONT_BODY, color: { argb: RED_700 } };
        }
      }
    });
  });

  if (uniqueDates.length) {
    styleBodyRows(ws, startRow, startRow + uniqueDates.length - 1, headers.length);
  }

  ws.views = [{ state: 'frozen', xSplit: 1, ySplit: headerRowIdx, showGridLines: false }];
}


// ---------- Sheet 4 : Averages ----------
function buildAveragesSheet({ wb, rows, uniqueResellers, productLabel, range, t }) {
  const ws = wb.addWorksheet(sheetName(t('exportSheet.averages')), {
    views: [{ showGridLines: false }],
  });

  const headers = [
    t('exportAverages.reseller'),
    t('exportAverages.avg'),
    t('exportAverages.min'),
    t('exportAverages.max'),
    t('exportAverages.days'),
  ];
  ws.columns = [
    { width: 26 }, { width: 22 }, { width: 18 }, { width: 18 }, { width: 18 },
  ];

  addTitleBand(ws, headers.length, `${t('exportSheet.averages')} — ${productLabel}`, `${t('exportSummary.period')}: ${t(`timeRange.${range}`) || range}`);

  const headerRowIdx = 4;
  const headerRow = ws.getRow(headerRowIdx);
  headers.forEach((h, i) => (headerRow.getCell(i + 1).value = h));
  styleHeaderRow(ws, headerRowIdx, headers.length);

  const stats = uniqueResellers.map(rs => {
    const values = rows.filter(r => r.hostname === rs).map(r => parseFloat(r.discount)).filter(n => !Number.isNaN(n));
    if (!values.length) return { hostname: rs, avg: null, min: null, max: null, days: 0 };
    const days = new Set(rows.filter(r => r.hostname === rs).map(r => r.date)).size;
    return {
      hostname: rs,
      avg: Math.round((values.reduce((s, n) => s + n, 0) / values.length) * 100) / 100,
      min: Math.round(Math.min(...values) * 100) / 100,
      max: Math.round(Math.max(...values) * 100) / 100,
      days,
    };
  }).sort((a, b) => (b.avg ?? -1) - (a.avg ?? -1));

  const startRow = headerRowIdx + 1;
  stats.forEach((s, idx) => {
    const rowRef = ws.getRow(startRow + idx);
    rowRef.getCell(1).value = s.hostname;
    rowRef.getCell(2).value = s.avg;
    rowRef.getCell(3).value = s.min;
    rowRef.getCell(4).value = s.max;
    rowRef.getCell(5).value = s.days;
    rowRef.getCell(2).numFmt = FMT_PCT_TXT;
    rowRef.getCell(3).numFmt = FMT_PCT_TXT;
    rowRef.getCell(4).numFmt = FMT_PCT_TXT;
    rowRef.getCell(5).numFmt = FMT_INT;
    if (typeof s.avg === 'number' && s.avg > 0) {
      rowRef.getCell(2).font = { ...FONT_BODY, bold: true, color: { argb: RED_700 } };
    }
  });

  if (stats.length) {
    styleBodyRows(ws, startRow, startRow + stats.length - 1, headers.length);
  }

  ws.autoFilter = {
    from: { row: headerRowIdx, column: 1 },
    to: { row: headerRowIdx + Math.max(stats.length, 1), column: headers.length },
  };
  ws.views = [{ state: 'frozen', xSplit: 0, ySplit: headerRowIdx, showGridLines: false }];
}
